import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { Modal, Placeholder } from 'semantic-ui-react';
import _ from 'lodash';
import styled from 'styled-components';
import BasicProgressPlaceholder from '@aws-ee/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@aws-ee/base-ui/dist/parts/helpers/ErrorBox';
import SurveyContent from './parts/SurveyContent';

const SurveyModal = styled(Modal)`
  width: 100% !important;
  height: 100%;
  margin: 0px !important;
  top: 0px;
  background: #f5f5f5 !important;
  overflow: hidden;
`;
const Survey = styled.div`
  height: 100%;
`;
const SurveyHeader = styled.div`
  height: 50px;
  margin: 0px 150px;

  img {
    width: 210px;
    position: absolute;
    left: 24px;
    cursor: pointer;
  }

  h1 {
    font-family: 'IBM Plex Sans';
    font-weight: 700;
    font-size: 20px;
    line-height: 20px;
    color: #2e2d29;
    margin: 0px;
  }
`;

const AssignedProject = styled.div`
  margin: 0px 150px 24px 150px;
  padding: 20px 24px;
  border: 1px solid #f6e9db;
  border-left: 4px solid #8c1515;
  border-radius: 4px;
  background: #fffaf1;
  font-family: 'IBM Plex Sans';
  color: #2e2d29;

  h2 {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #8c1515;
    margin: 0px 0px 10px 0px;
  }

  dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 16px;
    row-gap: 6px;
    margin: 0px;
    font-size: 15px;
    line-height: 145%;
  }

  dt {
    font-weight: 700;
    color: #5c6573;
  }

  dd {
    margin: 0px;
  }
`;

const SurveyPage = ({ match, assets, learningStore, surveyStore }) => {
  const params = match.params;

  const [module, setModule] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    const getUserLearning = async id => {
      setLoading(true);
      const userLearning = await learningStore.getUserLearning(id);
      if (!_.isNil(userLearning) && userLearning.id === params.userLearningId && userLearning.paths.length > 0) {
        const pathIndex = userLearning.paths.findIndex(m => m.id === params.pathId);
        if (pathIndex > -1) {
          const moduleIndex = userLearning.paths[pathIndex].modules.findIndex(m => m.id === params.moduleId);
          if (moduleIndex > -1) {
            const module = userLearning.paths[pathIndex].modules[moduleIndex];
            setModule(module);
          } else setError(`Module with id ${params.moduleId} doesn't exist`);
        } else setError(`Path with id ${params.pathId} doesn't exist`);
      } else setError(`User learning with id ${params.userLearningId} doesn't exist`);
      setLoading(false);
    };

    getUserLearning(params.userLearningId).catch(error => {
      setLoading(false);
      setError(error.message);
    });
  }, [params]);

  useEffect(() => {
    const getSurvey = async id => {
      setLoading(true);
      const survey = await surveyStore.getSurvey(id);
      setSurvey(survey);
      setLoading(false);
    };

    if (!_.isNil(module) && !_.isNil(module.surveyId))
      getSurvey(module.surveyId).catch(error => {
        setLoading(false);
        setError(error.message);
      });
  }, [module]);

  // The survey opens in its own tab, so nothing can be handed down as a prop -- the assigned
  // project has to be fetched here. Best-effort: a failure hides the header rather than blocking
  // the instrument.
  useEffect(() => {
    let cancelled = false;
    learningStore
      .fetchProjectAssignmentStatus(params.userLearningId)
      .then(a => {
        if (!cancelled) setAssignment(a);
      })
      .catch(() => {
        if (!cancelled) setAssignment(null);
      });
    return () => {
      cancelled = true;
    };
  }, [params.userLearningId]);

  const projectMetadata = assignment && assignment.projectMetadata;
  const showAssignedProject = !_.isNil(survey) && survey.showAssignedProject && !_.isNil(projectMetadata);

  let content = null;
  if (error) {
    content = <ErrorBox error={error} className="mt3 mr0 ml0" />;
  } else if (loading) {
    content = <BasicProgressPlaceholder segmentCount={5} />;
  } else if (!_.isNil(survey)) {
    content = (
      <SurveyContent
        survey={survey}
        userLearningId={params.userLearningId}
        pathId={params.pathId}
        moduleId={params.moduleId}
      />
    );
  }

  return (
    <SurveyModal className="pt3 px3" open>
      <Survey className="flex flex-column">
        <SurveyHeader className="flex items-center justify-center pb4 pt3">
          <img
            src={assets.images.logoImage}
            alt="Stanford Data Ocean Logo"
            onClick={() => {
              window.location = window.location.origin;
            }}
          />
          {!_.isNil(module) ? (
            <h1>{module.name}</h1>
          ) : (
            <div className="col-3">
              <Placeholder>
                <Placeholder.Header>
                  <Placeholder.Line />
                </Placeholder.Header>
              </Placeholder>
            </div>
          )}
        </SurveyHeader>
        {showAssignedProject && (
          <AssignedProject>
            <h2>Your assigned project</h2>
            <dl>
              <dt>Project number</dt>
              <dd>{projectMetadata.projectNumber}</dd>
              <dt>Paper</dt>
              <dd>{projectMetadata.paperTitle}</dd>
              <dt>Authors</dt>
              <dd>{projectMetadata.authors}</dd>
              <dt>DOI</dt>
              <dd>{projectMetadata.doi}</dd>
            </dl>
          </AssignedProject>
        )}
        {content}
      </Survey>
    </SurveyModal>
  );
};

export default inject('learningStore', 'surveyStore')(withRouter(observer(SurveyPage)));
